import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { HR_DOCUMENT_STATUSES, HrDocumentCenter, HrDocumentComplianceRow } from '../hr.models';
import { HrService } from '../hr.service';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-document-center',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-document-center.component.html',
  styleUrls: ['./hr-document-center.component.scss']
})
export class HrDocumentCenterComponent implements OnInit {
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  center: HrDocumentCenter = {};
  status?: string;
  workerCategory?: string;

  readonly statusOptions = HR_DOCUMENT_STATUSES.map(value => ({ label: this.format(value), value }));
  workerCategoryOptions: { label: string; value: string }[] = [];
  readonly canManageDocuments = this.permissionService.hasPermission(app_permissions.hr.documents.manage);

  ngOnInit(): void {
    this.hrService.workerCategories().subscribe(response => {
      this.workerCategoryOptions = (response.data ?? []).map(item => ({
        label: item.categoryName || this.format(item.categoryCode), value: item.categoryCode || ''
      }));
    });
    this.load();
  }

  get rows(): HrDocumentComplianceRow[] {
    return this.center.rows || [];
  }

  get metrics(): PageMetric[] {
    const summary = this.center.summary || {};
    return [
      { label: 'Valid', value: summary.validDocuments ?? 0, tone: 'success' },
      { label: 'Expiring', value: summary.expiringSoon ?? 0, tone: 'warn' },
      { label: 'Expired', value: summary.expired ?? 0, tone: 'danger' },
      { label: 'Missing', value: summary.missingRequired ?? 0, tone: 'danger' },
      { label: 'Pending Verification', value: summary.pendingVerification ?? 0, tone: 'info' },
      { label: 'Rejected', value: summary.rejected ?? 0, tone: 'danger' },
      { label: 'Waived', value: summary.waived ?? 0 }
    ];
  }

  load(): void {
    this.loading = true;
    this.hrService.documentCenter(undefined, this.status, this.workerCategory).subscribe({
      next: response => this.center = response.data ?? {},
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Documents', detail: 'Unable to load document center.' }),
      complete: () => this.loading = false
    });
  }

  evaluate(): void {
    if (!this.canManageDocuments) return;
    this.loading = true;
    this.hrService.evaluateDocuments().subscribe({
      next: response => {
        this.messageService.add({
          severity: 'success',
          summary: 'HR Documents',
          detail: `${response.data?.snapshotsCreated ?? 0} compliance rows evaluated.`
        });
        this.load();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'HR Documents', detail: 'Unable to evaluate documents.' });
      }
    });
  }

  clearFilters(): void {
    this.status = undefined;
    this.workerCategory = undefined;
    this.load();
  }

  daysLabel(row: HrDocumentComplianceRow): string {
    if (row.daysUntilExpiry === undefined || row.daysUntilExpiry === null) return 'No expiry';
    if (row.daysUntilExpiry < 0) return `${Math.abs(row.daysUntilExpiry)} days overdue`;
    if (row.daysUntilExpiry === 0) return 'Expires today';
    return `${row.daysUntilExpiry} days left`;
  }

  statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'VALID' || status === 'WAIVED') return 'success';
    if (status === 'EXPIRING_SOON' || status === 'PENDING_VERIFICATION') return 'warn';
    if (status === 'EXPIRED' || status === 'MISSING' || status === 'REJECTED') return 'danger';
    return 'secondary';
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }
}
